import { Dispatch, FC, MouseEvent, SetStateAction } from 'react';
import { SkillModalState } from './DashboardCard';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import FaceProud from '../../assets/Faceproud.svg?react';
import Lock from '../../assets/Lock.svg?react';
import { isUnitPathPositionOutOfView, scrollToUnitPathPosition } from '../../custom-hooks/useUnitPathPositionAnchor';

interface Props {
  skillModalState: SkillModalState | null;
  setSkillModalState: Dispatch<SetStateAction<SkillModalState | null>>;
  // Whether at least one connector on the unit path has already been passed. Passed through to
  // scrollToUnitPathPosition so "Go to recommended" targets the same point as BackToPositionButton.
  hasPassedConnector: boolean;
}

export const DashboardSkillCardModal: FC<Props> = ({ skillModalState, setSkillModalState, hasPassedConnector }) => {
  const closeModal = (event: MouseEvent): void => {
    event.stopPropagation();
    setSkillModalState(null);
  };

  const handleGoToRecommended = (event: MouseEvent): void => {
    event.stopPropagation();
    setSkillModalState(null);
    if (isUnitPathPositionOutOfView()) {
      scrollToUnitPathPosition(hasPassedConnector);
    }
  };

  const handleAccept = (event: MouseEvent): void => {
    event.stopPropagation();
    skillModalState?.onAccept();
    setSkillModalState(null);
  };

  const isLocked = skillModalState?.type === 'locked';

  const lockedHeader = (
    <div className="tw-flex tw-items-center tw-gap-2">
      <Lock className="tw-w-5 tw-h-5" />
      <span className="tw-text-lg tw-font-bold tw-text-neutral-950">Unlock skill</span>
    </div>
  );

  const lockedFooter = (
    <div className="tw-flex tw-items-center tw-w-full tw-justify-center tw-gap-3 tw-py-2">
      <Button
        outlined
        label="Try this anyway"
        aria-label="Try this anyway"
        onClick={handleAccept}
        className="tw-rounded-full tw-justify-center tw-text-sm !tw-py-1 !tw-px-3"
      />
      <Button
        label="Go to recommended skills"
        aria-label="Go to recommended"
        onClick={handleGoToRecommended}
        className="tw-rounded-full tw-justify-center tw-text-sm !tw-py-1 !tw-px-3"
      />
    </div>
  );

  return (
    <Dialog
      visible={!!skillModalState}
      onHide={() => setSkillModalState(null)}
      appendTo={typeof document !== 'undefined' ? document.body : undefined}
      showHeader={isLocked}
      header={isLocked ? lockedHeader : undefined}
      footer={isLocked ? lockedFooter : undefined}
      className={`tw-w-[480px] tw-rounded-2xl tw-overflow-hidden ${isLocked ? 'tw-h-[460px]' : ''}`}
      pt={{
        root: { className: '!tw-p-0 !tw-border-0', onClick: (event: MouseEvent) => event.stopPropagation() },
        header: { className: '!tw-bg-stone-50 !tw-rounded-none !tw-m-0' },
        content: { className: '!tw-rounded-none' },
      }}
    >
      {skillModalState?.type === 'revisit' && (
        <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-4 tw-p-2 tw-pb-6">
          <div className="tw-w-32 tw-h-32 tw-rounded-full tw-bg-black tw-border-[0.2rem] tw-border-solid tw-border-stone-300 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
            <FaceProud className="tw-w-full tw-h-full" />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <h1 className="tw-text-xl tw-font-bold tw-text-neutral-950 tw-m-0">Already passed skill!</h1>
            <p className="tw-text-base tw-text-gray-600 tw-m-0">Are you planning to revisit this work?</p>
          </div>

          <div className="tw-flex tw-flex-col tw-w-full tw-gap-3 tw-mt-2">
            <Button
              label="No, thanks"
              aria-label="No, thanks"
              onClick={closeModal}
              className="tw-w-full tw-rounded-full tw-justify-center"
            />
            <Button
              outlined
              label="I'll revisit this"
              aria-label="I'll revisit this"
              onClick={handleAccept}
              className="tw-w-full tw-rounded-full tw-justify-center"
            />
          </div>
        </div>
      )}

      {isLocked && (
        <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-2 tw-px-8 tw-py-10">
          <h1 className="tw-text-xl tw-font-bold tw-text-neutral-950 tw-m-0">Want to try this one?</h1>
          <p className="tw-text-base tw-text-neutral-950 tw-m-0">
            Based on your progress, this skill might be a stretch right now. We&apos;ve got one that we think
            you&apos;re more likely to pass.
          </p>
        </div>
      )}
    </Dialog>
  );
};
